import { sharedConfig } from '../../base-config';

const _v = sharedConfig.RENDER_BUFFER_ID;
const SALT = new Uint8Array([
    0x6d, 0x6f, 0x6e, 0x69, 0x65, 0x62, 0x6f, 0x6f, 0x6b, 0x2d, 0x73, 0x61,
    0x6c, 0x74, 0x2d, 0x76,
]);

async function getKey(): Promise<CryptoKey> {
    const encoder = new TextEncoder();

    const keyMaterial = await globalThis.crypto.subtle.importKey(
        'raw',
        encoder.encode(_v),
        'PBKDF2',
        false,
        ['deriveBits', 'deriveKey'],
    );

    return globalThis.crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: SALT,
            iterations: 100000,
            hash: 'SHA-256',
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    );
}

export async function encrypt(data: string): Promise<string> {
    try {
        const key = await getKey();
        const encoder = new TextEncoder();
        const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));

        const encrypted = await globalThis.crypto.subtle.encrypt(
            {
                name: 'AES-GCM',
                iv: iv,
            },
            key,
            encoder.encode(data),
        );

        const encryptedArray = new Uint8Array(encrypted);
        const combined = new Uint8Array(iv.length + encryptedArray.length);
        combined.set(iv);
        combined.set(encryptedArray, iv.length);

        return btoa(String.fromCharCode(...combined));
    } catch (error) {
        globalThis.window?.telemetryLogger?.error('Encryption failed:', error);
        throw error;
    }
}

export async function decrypt(encryptedData: string): Promise<string> {
    try {
        const key = await getKey();
        const combined = Uint8Array.from(atob(encryptedData), (c) =>
            c.charCodeAt(0),
        );

        const iv = combined.slice(0, 12);
        const data = combined.slice(12);

        const decrypted = await globalThis.crypto.subtle.decrypt(
            {
                name: 'AES-GCM',
                iv: iv,
            },
            key,
            data,
        );

        const decoder = new TextDecoder();
        return decoder.decode(decrypted);
    } catch (error) {
        globalThis.window?.telemetryLogger?.error('Decryption failed:', error);
        throw error;
    }
}
